import InputField from 'components/fields/InputField';
import { useNavigate } from 'react-router-dom';

/** validation de formulaire */
import { Box } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import LanguageSwitcher from 'components/LanguageSwitcher';
import Error from 'components/message/Error';
import useCustomSignin from 'hooks/useCunstomSigin';
import usePostAxiosData from 'hooks/usePostAxiosData';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next'; // Importer useTranslation
import { Transition } from 'views/admin/enterprises/AddEnterprise';
import * as yup from 'yup';

export default function SignIn() {
  const { t } = useTranslation('sign_in'); // Utilisation du namespace "sign_in"

  /** Form validation config */
  const schema = yup.object().shape({
    userId: yup.string().required(t('phone_required')), // Traduit
    password: yup.string().required(t('password_required')), // Traduit
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const onSubmitHandler = (data) => {
    handleLogin(data);
  };

  /** redirection  */
  const customSignin = useCustomSignin();
  function handleSignin(data) {
    customSignin(data, '/admin/stats');
  }

  const [loading, postAxiosData, errorMassage, successMessage, result] = usePostAxiosData(
    `auth/login`,
    'POST',
    false
  );

  const handleLogin = async (dataYub) => {
    const data = {
      phone: dataYub?.userId,
      password: dataYub?.password,
    };

    await postAxiosData(data);
  };

  /** gestion des erreurs et succès */
  useEffect(() => {
    if (successMessage?.status === 200 || successMessage?.status === 201) {
      toast.success(`${successMessage?.message} `, {
        duration: 4000,
        icon: '👏',
        className: 'bg-success text-white',
      });

      handleSignin(result);
    }
  }, [successMessage, handleSignin, result]); // Ajout de dépendances

  const navigate = useNavigate();
  useEffect(() => {
    if (errorMassage?.data?.isEmailVerified === false) navigate('/auth/email-verification');
  }, [errorMassage, navigate]); // Ajout de dépendances

  const [open, setOpen] = useState(false);
  return (
    <>
      <Box sx={{ position: 'absolute', top: -20, right: 0, mt: 5, mr: 15 }}>
        <LanguageSwitcher />
      </Box>
      <ResetPasswordModal open={open} onClose={() => setOpen(false)} />

      <div className="mb-16 flex h-full w-full items-center justify-center px-2 md:mx-0 md:px-0 lg:mb-10 lg:items-center lg:justify-start relative">
        <div className=" w-full max-w-full flex-col items-center md:pl-4 lg:pl-0 xl:max-w-[420px]">
          {errorMassage?.message && (
            <Error message={errorMassage?.message !== '' && errorMassage?.message} />
          )}
          <h4 className="mb-2.5 mt-2.5 text-4xl font-bold text-green-500 dark:text-white">
            {t('sign_in_title')} {/* Traduit */}
          </h4>
          <p className="mb-9 ml-1 text-base text-white">
            {t('sign_in_subtitle')} {/* Traduit */}
          </p>

          <form onSubmit={handleSubmit(onSubmitHandler)} className="flex flex-col gap-4">
            {/* Email */}
            <InputField
              register={register}
              variant="auth"
              extra="mb-3"
              label={t('phone_label')}
              name="userId"
              autoComplete={false}
              color={'white'}
              placeholder={t('phone_placeholder')}
              id="userId"
              type="text"
              error={errors.userId?.message !== '' ? errors.userId?.message : ''}
            />

            {/* Password */}
            <InputField
              register={register}
              variant="auth"
              extra="mb-3"
              label={t('password_label')}
              color={'white'}
              placeholder={t('password_placeholder')}
              id="password"
              name="password"
              type="password"
              error={errors.password?.message !== '' ? errors.password?.message : ''}
            />
            {/* Checkbox */}
            <div className="mb-4 flex items-center justify-between px-2">
              <a
                className="text-sm font-medium text-brand-500 hover:text-brand-600 dark:text-white"
                onClick={() => setOpen(true)}
              >
                {t('forgot_password')} {/* Traduit */}
              </a>
            </div>
            {loading ? (
              <button
                type="button"
                className="flex justify-center bg-indigo-400 linear mt-2 w-full rounded-[20px] bg-brand-500 py-[12px] text-base font-medium text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200"
                disabled
              >
                <svg className="mr-3 h-5 w-5 animate-spin" viewBox="0 0 24 24"></svg>
                {t('loading_button')} {/* Traduit */}
              </button>
            ) : (
              <button
                type="submit"
                className="linear mt-2 w-full rounded-[20px] bg-brand-500 py-[12px] text-base font-medium text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200"
              >
                {t('sign_in_button')} {/* Traduit */}
              </button>
            )}
          </form>
        </div>
      </div>
    </>
  );
}

const ResetPasswordModal = ({ open, onClose }) => {
  const { t } = useTranslation('sign_in'); // Utilisation du namespace "sign_in"
  const [loading, postAxiosData, successMessage] = usePostAxiosData(`auth/reset-password`, 'POST');

  const schema = yup.object().shape({
    email: yup
      .string()
      .email(t('email_invalid')) // Traduit
      .required(t('email_required')), // Traduit
    password: yup
      .string()
      .min(8, t('password_min_length')) // Traduit
      .matches(/[a-z]/, t('password_lowercase')) // Traduit
      .matches(/[A-Z]/, t('password_uppercase')) // Traduit
      .matches(/\d/, t('password_digit')) // Traduit
      .matches(/[!@#$%^&*(),.?":{}|<>]/, t('password_special_char')) // Traduit
      .required(t('password_required')), // Traduit
    confirmPassword: yup
      .string()
      .oneOf([yup.ref('password'), null], t('password_match')) // Traduit
      .required(t('password_required')), // Traduit
  });

  const {
    register,
    handleSubmit,
    resetField,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const onSubmitHandler = (data) => {
    handleLogin(data);
  };

  const handleLogin = async (dataYub) => {
    const data = {
      email: dataYub?.email,
      newPassword: dataYub?.password,
    };
    await postAxiosData(data);
  };

  useEffect(() => {
    if (successMessage) {
      resetField('email');
      resetField('password');
      resetField('confirmPassword');
      onClose();
      // toast.success(`${t("password_reset_success")}`, {
      //   duration: 4000,
      //   className: "bg-success text-white",
      // });
    }
  }, [successMessage, onClose, resetField, t]);

  const handleCloseInside = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth={true}
      maxWidth={'md'}
      aria-labelledby="customized-dialog-title"
      TransitionComponent={Transition}
    >
      <DialogTitle>{t('confirmation_title')}</DialogTitle> {/* Traduit */}
      <form onSubmit={handleSubmit(onSubmitHandler)}>
        <DialogContent
          style={{
            backdropFilter: 'blur(6px)',
            backgroundColor: ' rgba(5,5,5,0.4)',
          }}
          className="bg-gray-600"
        >
          <DialogContentText variant="h5">
            {t('reset_password_text')} {/* Traduit */}
          </DialogContentText>
          <InputField
            register={register}
            variant="auth"
            extra="mb-3"
            label={t('email_label')} // Traduit
            name="email"
            autoComplete={false}
            color={'white'}
            placeholder="example@example.com" // Placeholder pour l'email
            id="email"
            type="text"
            error={errors.email?.message !== '' ? errors.email?.message : ''}
          />

          {/* Password */}
          <InputField
            register={register}
            variant="auth"
            extra="mb-3"
            label={t('new_password_label')} // Traduit
            color={'white'}
            placeholder={t('password_placeholder')} // Traduit
            id="password"
            name="password"
            type="password"
            error={errors.password?.message !== '' ? errors.password?.message : ''}
          />
          <InputField
            register={register}
            variant="auth"
            extra="mb-3"
            label={t('confirm_password_label')} // Traduit
            color={'white'}
            placeholder={t('password_placeholder')} // Traduit
            id="confirmPassword"
            name="confirmPassword"
            type="password"
            error={errors.confirmPassword?.message !== '' ? errors.confirmPassword?.message : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseInside} color="error" variant="outlined">
            {t('cancel_button')}
          </Button>
          <Button type="submit" color="success" variant="contained" autoFocus isLoading={loading}>
            {t('continue_button')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
